import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { INVERSE, REMOVES_FROM_LIST, applyOptimistic, flatten, patchPages, type MessagePages } from '../lib/cache'
import { useUi } from '../store/ui'
import { api, qs, setCsrf } from './client'
import type { CalEvent, CalendarInfo, Contact, ContactInput, EventInput } from './types'
import type { Action, ActionResult, Filter, Folder, Label, MessageDetail, MessagePage, MessageSummary, OutboxJob, ReplyContext, Settings, User } from './types'

export interface ListParams { folder?: string; label?: string; q?: string; threads?: boolean; deep?: boolean }

export const keys = {
  me: ['me'] as const,
  folders: ['folders'] as const,
  labels: ['labels'] as const,
  messages: (p: ListParams) => ['messages', p] as const,
  message: (id: string) => ['message', id] as const,
  thread: (id: string) => ['thread', id] as const,
  settings: ['settings'] as const,
  filters: ['filters'] as const,
  outbox: ['outbox'] as const,
}

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: async () => { const r = await api<{ user: User; csrf: string }>('/auth/me'); setCsrf(r.csrf); return r.user },
    retry: false,
    staleTime: Infinity,
  })

export const useFolders = () => useQuery({ queryKey: keys.folders, queryFn: async () => (await api<{ folders: Folder[] }>('/folders')).folders, staleTime: 30_000 })
export const useLabels = () => useQuery({ queryKey: keys.labels, queryFn: async () => (await api<{ labels: Label[] }>('/labels')).labels, staleTime: 30_000 })
export function useSettings() {
  const me = useMe()
  return useQuery({ queryKey: keys.settings, queryFn: () => api<Settings>('/settings'), staleTime: Infinity, enabled: Boolean(me.data) && !me.data?.setupRequired })
}
export const useFilters = () => useQuery({ queryKey: keys.filters, queryFn: async () => (await api<{ filters: Filter[] }>('/filters')).filters })

export function useMessages(params: ListParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.messages(params),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<MessagePage>(`${params.q ? '/search' : '/messages'}${qs({ folder: params.folder, label: params.label, q: params.q, threads: params.threads, deep: params.deep, cursor: pageParam, limit: 50 })}`, { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  })
}

export const useMessage = (id: string | undefined) =>
  useQuery({ queryKey: keys.message(id ?? ''), queryFn: () => api<MessageDetail>(`/messages/${id}`), enabled: Boolean(id), staleTime: 5 * 60_000 })

export const useThread = (threadId: string | null | undefined, enabled: boolean) =>
  useQuery({ queryKey: keys.thread(threadId ?? ''), queryFn: async () => (await api<{ messages: MessageSummary[] }>(`/threads/${threadId}`)).messages, enabled: Boolean(threadId) && enabled })

export const usePrefetchMessage = () => {
  const qc = useQueryClient()
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Only prefetch after the pointer rests on a row: sweeping the list must not fire a mail-server fetch per row.
  return useCallback((id: string) => {
    clearTimeout(timer.current)
    if (qc.getQueryData(keys.message(id))) return
    timer.current = setTimeout(() => void qc.prefetchQuery({ queryKey: keys.message(id), queryFn: () => api<MessageDetail>(`/messages/${id}`), staleTime: 5 * 60_000 }), 350)
  }, [qc])
}

export const useOutbox = () => useQuery({ queryKey: keys.outbox, queryFn: async () => (await api<{ jobs: OutboxJob[] }>('/outbox')).jobs, refetchInterval: false })
/** Messages waiting for their scheduled time (or that failed when it arrived). */
export const useScheduled = () => { const q = useOutbox(); return { ...q, jobs: (q.data ?? []).filter((j) => j.payload.sendAt && (j.status === 'QUEUED' || j.status === 'FAILED')) } }

export const useReplyContext = () => useMutation({ mutationFn: ({ id, mode }: { id: string; mode: 'reply' | 'replyAll' | 'forward' }) => api<ReplyContext>(`/messages/${id}/reply-context${qs({ mode })}`) })

// ---- optimistic message mutations --------------------------------------------------------------------------------

function snapshotMessages(qc: QueryClient) {
  return qc.getQueriesData<MessagePages>({ queryKey: ['messages'] })
}

function findCached(qc: QueryClient, ids: string[]): MessageSummary[] {
  const want = new Set(ids)
  const seen = new Map<string, MessageSummary>()
  for (const [, data] of snapshotMessages(qc)) for (const m of flatten(data)) if (want.has(m.id)) seen.set(m.id, m)
  return [...seen.values()]
}

export function useActions() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)

  const mutation = useMutation({
    mutationFn: (v: { ids: string[]; action: Action; destination?: string }) => api<ActionResult>('/messages/actions', { method: 'POST', body: v }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['messages'] })
      const snapshot = snapshotMessages(qc)
      const ids = new Set(v.ids)
      qc.setQueriesData<MessagePages>({ queryKey: ['messages'] }, (d) =>
        patchPages(d, (m) => {
          if (!ids.has(m.id)) return m
          return REMOVES_FROM_LIST.has(v.action) ? null : applyOptimistic(m, v.action)
        }),
      )
      for (const id of v.ids) qc.setQueryData<MessageDetail>(keys.message(id), (d) => (d && !REMOVES_FROM_LIST.has(v.action) ? { ...d, seen: v.action === 'read' ? true : v.action === 'unread' ? false : d.seen, starred: v.action === 'star' ? true : v.action === 'unstar' ? false : d.starred } : d))
      return { snapshot }
    },
    onError: (err, _v, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)) // rollback
      notify({ message: err instanceof Error ? err.message : 'Action failed', severity: 'error' })
    },
    onSettled: () => {
      // The optimistic cache is already right; refetch lists only when the user next looks at them.
      void qc.invalidateQueries({ queryKey: ['messages'], refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: keys.folders })
      void qc.invalidateQueries({ queryKey: keys.labels })
    },
  })

  /** Runs an action and offers Undo (flag actions invert; moves go back to each message's previous folder). */
  const run = useCallback(
    (ids: string[], action: Action, opts: { toast?: string; destination?: string } = {}) => {
      const before = findCached(qc, ids)
      mutation.mutate({ ids, action, destination: opts.destination })
      if (!opts.toast) return
      const inverse = INVERSE[action]
      const undo = () => {
        if (inverse) return mutation.mutate({ ids, action: inverse })
        const byFolder = new Map<string, string[]>()
        for (const m of before) byFolder.set(m.folder, [...(byFolder.get(m.folder) ?? []), m.id])
        for (const [folder, group] of byFolder) mutation.mutate({ ids: group, action: 'move', destination: folder })
      }
      if (inverse || (before.length && action !== 'delete')) notify({ message: opts.toast, actionLabel: 'Undo', onAction: undo })
      else notify({ message: opts.toast })
    },
    [mutation, notify, qc],
  )
  return { run, isPending: mutation.isPending }
}

export function useSetLabels() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  const m = useMutation({
    mutationFn: (v: { messageIds: string[]; add: string[]; remove: string[] }) => api<ActionResult>('/messages/labels', { method: 'POST', body: v }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['messages'] })
      const snapshot = snapshotMessages(qc)
      const ids = new Set(v.messageIds)
      qc.setQueriesData<MessagePages>({ queryKey: ['messages'] }, (d) =>
        patchPages(d, (msg) => (ids.has(msg.id) ? { ...msg, labelIds: [...new Set([...msg.labelIds.filter((l) => !v.remove.includes(l)), ...v.add])] } : msg)),
      )
      return { snapshot }
    },
    onError: (err, _v, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data))
      notify({ message: err instanceof Error ? err.message : 'Could not update labels', severity: 'error' })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] })
      void qc.invalidateQueries({ queryKey: keys.labels })
    },
  })
  return {
    apply: (messageIds: string[], add: string[], remove: string[], toast = true) => {
      m.mutate({ messageIds, add, remove })
      if (toast) notify({ message: 'Labels updated', actionLabel: 'Undo', onAction: () => m.mutate({ messageIds, add: remove, remove: add }) })
    },
  }
}

// ---- calendar + contacts --------------------------------------------------------------------------------------------

export const useCalendars = () => useQuery({ queryKey: ['calendars'], queryFn: async () => (await api<{ calendars: CalendarInfo[] }>('/calendars')).calendars, staleTime: 5 * 60_000, retry: false })

export const useCalendarEvents = (from: Date, to: Date, enabled = true) =>
  useQuery({
    queryKey: ['calendar-events', from.toISOString(), to.toISOString()],
    enabled,
    queryFn: async () => (await api<{ events: CalEvent[] }>(`/calendar/events${qs({ from: from.toISOString(), to: to.toISOString() })}`)).events,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
  })

export function useEventMutations() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  const done = (msg: string) => () => { notify({ message: msg, severity: 'success' }); void qc.invalidateQueries({ queryKey: ['calendar-events'] }) }
  const fail = (e: unknown) => notify({ message: e instanceof Error ? e.message : 'Calendar request failed', severity: 'error' })
  return {
    create: useMutation({ mutationFn: (v: EventInput) => api('/calendar/events', { method: 'POST', body: v }), onSuccess: done('Event created'), onError: fail }),
    update: useMutation({ mutationFn: (v: { href: string; input: EventInput }) => api(`/calendar/events${qs({ href: v.href })}`, { method: 'PUT', body: v.input }), onSuccess: done('Event updated'), onError: fail }),
    remove: useMutation({ mutationFn: (href: string) => api(`/calendar/events${qs({ href })}`, { method: 'DELETE' }), onSuccess: done('Event deleted'), onError: fail }),
  }
}

export const useContacts = () => useQuery({ queryKey: ['contacts'], queryFn: async () => (await api<{ contacts: Contact[] }>('/contacts')).contacts, staleTime: 5 * 60_000, retry: false })

export function useContactMutations() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  const done = (msg: string) => () => { notify({ message: msg, severity: 'success' }); void qc.invalidateQueries({ queryKey: ['contacts'] }) }
  const fail = (e: unknown) => notify({ message: e instanceof Error ? e.message : 'Contacts request failed', severity: 'error' })
  return {
    create: useMutation({ mutationFn: (v: ContactInput) => api('/contacts', { method: 'POST', body: v }), onSuccess: done('Contact saved'), onError: fail }),
    update: useMutation({ mutationFn: (v: { href: string; input: ContactInput }) => api(`/contacts${qs({ href: v.href })}`, { method: 'PATCH', body: v.input }), onSuccess: done('Contact saved'), onError: fail }),
    remove: useMutation({ mutationFn: (href: string) => api(`/contacts${qs({ href })}`, { method: 'DELETE' }), onSuccess: done('Contact deleted'), onError: fail }),
  }
}

export function useEmptyFolder() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  return useMutation({
    mutationFn: (folder: string) => api<{ deleted: number }>('/folders/empty', { method: 'POST', body: { folder } }),
    onSuccess: (r) => { notify({ message: `${r.deleted} message${r.deleted === 1 ? '' : 's'} permanently deleted`, severity: 'success' }); void qc.invalidateQueries({ queryKey: ['messages'] }); void qc.invalidateQueries({ queryKey: keys.folders }) },
    onError: (e) => notify({ message: e instanceof Error ? e.message : 'Could not empty folder', severity: 'error' }),
  })
}

export function useUpdateSettings() {
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  return useMutation({
    mutationFn: (v: Partial<Settings>) => api<Settings>('/settings', { method: 'PUT', body: v }),
    onMutate: async (v) => { qc.setQueryData<Settings>(keys.settings, (old) => (old ? { ...old, ...v } : old)) }, // instant theme switch
    onSuccess: (s) => qc.setQueryData(keys.settings, s),
    onError: (e) => { void qc.invalidateQueries({ queryKey: keys.settings }); notify({ message: e instanceof Error ? e.message : 'Could not save settings', severity: 'error' }) },
  })
}
